import type { InfiniteData } from "@tanstack/react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { LayoutHeader } from "./components/layout-header";
import { NotificationCard } from "./components/NotificationCard";
import { InboxNotificationCard } from "./components/InboxNotificationCard";
import { MissedCallNotificationCard } from "./components/MissedCallNotificationCard";
import { VoicemailNotificationCard } from "./components/VoicemailNotificationCard";
import { FraudAlertNotificationCard } from "./components/FraudAlertNotificationCard";
import { EscalatedConversationNotificationCard } from "./components/EscalatedConversationNotificationCard";
import { SmsNotDeliveredNotificationCard } from "./components/SmsNotDeliveredNotificationCard";
import { AICallFollowUpNotificationCard } from "./components/AICallFollowUpNotificationCard";
import { CallerNeedsHelpNotificationCard } from "./components/CallerNeedsHelpNotificationCard";
import { PhoneNumberPortNotificationCard } from "./components/PhoneNumberPortNotificationCard";
import { ProductRequestedNotificationCard } from "./components/ProductRequestedNotificationCard";
import { RentalVerificationNotificationCard } from "./components/RentalVerificationNotificationCard";
import { RentalVerificationFilters } from "./components/RentalVerificationFilters";
import {
  matchesRentalVerificationFilter,
  type RentalVerificationFilter
} from "./helpers/rental-verification";
import { IdvBypassNotificationCard } from "./components/IdvBypassNotificationCard";
import { ShowingRescheduleNotificationCard } from "./components/ShowingRescheduleNotificationCard";
import { NotificationGrouper } from "./helpers/grouper";
import { resolveTabs } from "./helpers/resolve-tabs";
import { showsProductNotificationTabs } from "./lib/access";
import {
  CommunicationSubTab,
  MarkAllAsReadRequest,
  MarkAllAsReadResponse,
  Notification,
  NotificationKind,
  NotificationTab,
  NotificationType,
  SHOWING_RESCHEDULE_NOTIFICATION_TYPES
} from "./types";
import { HugeiconsIcon } from "@hugeicons/react";
import { Notification01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Card } from "shared/components/Card";
import { Show } from "shared/components";
import { Tabs } from "shared/components/BaseTabs";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

const NOTIFICATIONS_PER_PAGE = 10;

type NotificationsPage = {
  items: Notification[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
  // Rental verifications tab only
  waiting_count?: number;
};

const grouper = new NotificationGrouper({
  locale: "en-US",
  weekStartsOn: 1,
  keyOf: (it) => String(it.id ?? `${it.created_at}|${it.user?.id ?? ""}`)
});

const notificationApi = ({
  kind,
  unreadOnly,
  page
}: {
  kind: NotificationKind;
  unreadOnly: boolean;
  page: number;
}): Promise<NotificationsPage> => {
  return instance
    .get("/portal/v1/notifications", {
      params: {
        kind,
        unread_only: unreadOnly,
        page,
        per_page: NOTIFICATIONS_PER_PAGE
      }
    })
    .then((res) => res.data);
};

const unreadNotificationCountApi = ({ kind }: { kind: NotificationKind }) => {
  return instance
    .get("/portal/v1/notifications/unread_count?", {
      params: {
        kind
      }
    })
    .then((res) => res.data);
};

const markPagesAsRead = (
  cached: InfiniteData<NotificationsPage> | undefined,
  readAt: string
) =>
  cached
    ? {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.read_at ? item : { ...item, read_at: readAt }
          )
        }))
      }
    : cached;

const markAllNotificationsAsReadApi = ({
  kind
}: MarkAllAsReadRequest): Promise<MarkAllAsReadResponse> => {
  return instance
    .post("/portal/v1/notifications/mark_all_as_read", { kind })
    .then((res) => res.data);
};

const tabKind = (
  tab: NotificationTab,
  sub: CommunicationSubTab
): NotificationKind => {
  if (tab === NotificationTab.Communication)
    return sub === CommunicationSubTab.Messages
      ? NotificationKind.Message
      : NotificationKind.Call;
  if (tab === NotificationTab.PropertyShield)
    return NotificationKind.PropertyShield;
  if (tab === NotificationTab.PhoneNumbers)
    return NotificationKind.PhoneNumbers;
  if (tab === NotificationTab.Products) return NotificationKind.Products;
  if (tab === NotificationTab.RentalVerification)
    return NotificationKind.RentalVerification;
  if (tab === NotificationTab.Application) return NotificationKind.Application;
  return tab === NotificationTab.Assigned
    ? NotificationKind.Assigned
    : NotificationKind.Mention;
};

const runInSequence = <T,>(
  items: T[],
  run: (item: T) => Promise<unknown>
): Promise<unknown> =>
  items.reduce<Promise<unknown>>(
    (previous, item) => previous.then(() => run(item).catch(() => undefined)),
    Promise.resolve()
  );

const flattenPages = (data: InfiniteData<NotificationsPage> | undefined) =>
  data?.pages.flatMap((page) => page.items) ?? [];

const newestFirst = (a: Notification, b: Notification) =>
  b.created_at.localeCompare(a.created_at);

export const BoomNotificationView = () => {
  const { auth, type, partner } = useAuthContext();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      tab: NotificationTab.Mention as string,
      sub: CommunicationSubTab.Calls as string
    },
    ["tab", "sub"]
  );

  // Seeing missed calls is visibility, not calling: they follow the same gate
  // as the /calls route so inbound-only users (no can_make_voice_calls) still
  // learn who tried to reach them. The "Call back" affordance inside the card
  // carries the stricter useVoiceEnabled() gate on its own.
  const showsMissedCalls =
    type.isCommunicationPhone && type.isProvisionedPhoneNumber;
  // Escalations and follow-up alerts come from the AI Leasing Agent; the API
  // hides them without it.
  const showsAiAgentCalls = Boolean(type.isCrmAiVoiceAgent);
  const showsCallsSubTab = showsMissedCalls || showsAiAgentCalls;

  const showPropertyShieldTab = Boolean(type.isListingFraudAlerts);
  // Bring your number alerts (CRM-1769) follow the phone numbers entitlement.
  const showPhoneNumbersTab = Boolean(type.isProvisionedPhoneNumber);
  // Product requests (PLT-725) go to admins and owners, who can enable them.
  const showProductsTab = Boolean(type.adminPrivileges && type.isBoomProducts);
  const showsProductTabs = showsProductNotificationTabs(type, partner);

  const { tab: pageTab, sub: subTab } = resolveTabs(
    searchParams.tab,
    searchParams.sub,
    {
      showsCalls: showsCallsSubTab,
      showsPropertyShield: showPropertyShieldTab,
      showsPhoneNumbers: showPhoneNumbersTab,
      showsProducts: showProductsTab,
      showsProductTabs
    }
  );
  const activeTab = tabKind(pageTab, subTab);
  // The Calls sub-tab replaces the AI Agents tab, so it lists those
  // escalations alongside missed calls and follow-up alerts (CRM-1593).
  const showsAiAgentKind =
    activeTab === NotificationKind.Call && showsAiAgentCalls;
  const activeKinds = showsAiAgentKind
    ? [NotificationKind.Call, NotificationKind.AiAgent]
    : [activeTab];

  // One unread-count request per kind; these could collapse into a single counts-by-kind query, which is also
  // a chance to tidy up the NotificationKind / NotificationTab / per-kind count types.
  const { data: unreadMentionCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Mention],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Mention
      })
  });

  const { data: unreadAssignedCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Assigned],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Assigned
      })
  });

  const { data: unreadMessageCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Message],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Message
      })
  });

  const { data: unreadCallCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Call],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Call
      }),
    enabled: showsCallsSubTab
  });

  const { data: unreadPropertyShieldCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.PropertyShield],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.PropertyShield
      }),
    enabled: showPropertyShieldTab
  });

  const { data: unreadPhoneNumbersCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.PhoneNumbers],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.PhoneNumbers
      }),
    enabled: showPhoneNumbersTab
  });

  const { data: unreadProductsCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Products],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Products
      }),
    enabled: showProductsTab
  });

  const { data: unreadAiAgentCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.AiAgent],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.AiAgent
      }),
    enabled: showsAiAgentCalls
  });

  const { data: unreadRentalVerificationCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: [
      "UNREAD_NOTIFICATIONS_COUNT",
      NotificationKind.RentalVerification
    ],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.RentalVerification
      })
  });

  const { data: unreadApplicationCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Application],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Application
      })
  });
  const unreadMessagesSubTabCount = unreadMessageCount?.total_unread || 0;
  const unreadAiAgentCalls = showsAiAgentCalls
    ? unreadAiAgentCount?.total_unread || 0
    : 0;
  const unreadCallsSubTabCount = showsCallsSubTab
    ? (unreadCallCount?.total_unread || 0) + unreadAiAgentCalls
    : 0;

  const notificationsQuery = useInfiniteQuery<NotificationsPage>({
    queryKey: ["NOTIFICATIONS", activeTab, unreadOnly],
    queryFn: ({ pageParam }) =>
      notificationApi({
        kind: activeTab,
        unreadOnly,
        page: pageParam as number
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined
  });

  const aiAgentQuery = useInfiniteQuery<NotificationsPage>({
    queryKey: ["NOTIFICATIONS", NotificationKind.AiAgent, unreadOnly],
    queryFn: ({ pageParam }) =>
      notificationApi({
        kind: NotificationKind.AiAgent,
        unreadOnly,
        page: pageParam as number
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: showsAiAgentKind
  });

  const notifications = useMemo(() => {
    const items = flattenPages(notificationsQuery.data);
    if (!showsAiAgentKind) return items;
    const aiAgentItems = flattenPages(aiAgentQuery.data);
    const loadedUntil = [
      notificationsQuery.hasNextPage ? items.at(-1)?.created_at : undefined,
      aiAgentQuery.hasNextPage ? aiAgentItems.at(-1)?.created_at : undefined
    ]
      .filter((createdAt): createdAt is string => Boolean(createdAt))
      .sort()
      .at(-1);
    const merged = [...items, ...aiAgentItems].sort(newestFirst);
    return loadedUntil
      ? merged.filter((item) => item.created_at >= loadedUntil)
      : merged;
  }, [
    notificationsQuery.data,
    notificationsQuery.hasNextPage,
    aiAgentQuery.data,
    aiAgentQuery.hasNextPage,
    showsAiAgentKind
  ]);

  const [rentalFilter, setRentalFilter] =
    useState<RentalVerificationFilter>("all");
  const isRentalVerificationTab =
    activeTab === NotificationKind.RentalVerification;

  useEffect(() => {
    if (!isRentalVerificationTab) setRentalFilter("all");
  }, [isRentalVerificationTab]);

  const visibleNotifications = useMemo(() => {
    if (!isRentalVerificationTab) return notifications;

    return notifications.filter((item) =>
      matchesRentalVerificationFilter(item, rentalFilter)
    );
  }, [isRentalVerificationTab, notifications, rentalFilter]);

  const loadWholeTab = isRentalVerificationTab && rentalFilter !== "all";

  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    rootMargin: "200px 0px",
    threshold: 0
  });

  useEffect(() => {
    if (!loadMoreInView && !loadWholeTab) return;
    const queries = showsAiAgentKind
      ? [notificationsQuery, aiAgentQuery]
      : [notificationsQuery];
    queries.forEach((query) => {
      if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
    });
  }, [
    loadMoreInView,
    loadWholeTab,
    showsAiAgentKind,
    notificationsQuery.hasNextPage,
    notificationsQuery.isFetchingNextPage,
    notificationsQuery.fetchNextPage,
    aiAgentQuery.hasNextPage,
    aiAgentQuery.isFetchingNextPage,
    aiAgentQuery.fetchNextPage
  ]);
  const { data: unread_notification_count } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", activeTab],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: activeTab
      }),
    enabled: Boolean(auth)
  });
  // Task/lead notifications the partner can't reach are hidden by the API, so the
  // list and every count here are already scoped.
  const unreadTotalCount = showsAiAgentKind
    ? unreadCallsSubTabCount
    : unread_notification_count?.total_unread || 0;

  const { mutateAsync: markAllAsRead, isPending: isMarkingAllAsRead } =
    useMutation({
      mutationKey: ["NOTIFICATIONS", activeTab, "MARK_ALL_AS_READ"],
      mutationFn: (kind: MarkAllAsReadRequest["kind"]) =>
        markAllNotificationsAsReadApi({ kind }),
      onMutate: async (kind) => {
        await Promise.all([
          queryClient.cancelQueries({ queryKey: ["NOTIFICATIONS", kind] }),
          queryClient.cancelQueries({
            queryKey: ["UNREAD_NOTIFICATIONS_COUNT", kind]
          }),
          queryClient.cancelQueries({
            queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
          })
        ]);

        const previousPages = queryClient.getQueriesData<
          InfiniteData<NotificationsPage>
        >({ queryKey: ["NOTIFICATIONS", kind] });
        const previousTabCount = queryClient.getQueryData<{
          total_unread: number;
        }>(["UNREAD_NOTIFICATIONS_COUNT", kind]);
        const previousTotalUnread = queryClient.getQueryData<number>([
          "TOTAL_UNREAD_NOTIFICATIONS"
        ]);

        const readAt = new Date().toISOString();
        queryClient.setQueriesData<InfiniteData<NotificationsPage>>(
          { queryKey: ["NOTIFICATIONS", kind] },
          (cached) => markPagesAsRead(cached, readAt)
        );
        queryClient.setQueryData(["UNREAD_NOTIFICATIONS_COUNT", kind], {
          total_unread: 0
        });
        queryClient.setQueryData<number>(
          ["TOTAL_UNREAD_NOTIFICATIONS"],
          (total) =>
            Math.max((total || 0) - (previousTabCount?.total_unread || 0), 0)
        );

        return { previousPages, previousTabCount, previousTotalUnread };
      },
      onError: (error, kind, context) => {
        context?.previousPages.forEach(([queryKey, data]) =>
          queryClient.setQueryData<InfiniteData<NotificationsPage> | undefined>(
            queryKey,
            data
          )
        );
        if (context?.previousTabCount)
          queryClient.setQueryData(
            ["UNREAD_NOTIFICATIONS_COUNT", kind],
            context.previousTabCount
          );
        if (context?.previousTotalUnread !== undefined)
          queryClient.setQueryData(
            ["TOTAL_UNREAD_NOTIFICATIONS"],
            context.previousTotalUnread
          );
        toast.error(getErrorMessage(error));
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          ["TOTAL_UNREAD_NOTIFICATIONS"],
          data.total_unread
        );
      },
      onSettled: (_data, _error, kind) => {
        queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS", kind] });
        queryClient.invalidateQueries({
          queryKey: ["UNREAD_NOTIFICATIONS_COUNT", kind]
        });
        queryClient.invalidateQueries({
          queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
        });
        queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
      }
    });
  const handleMarkAllAsRead = () =>
    runInSequence(activeKinds, (kind) => markAllAsRead(kind));

  const groupedNotifications = useMemo(
    () => grouper.group(visibleNotifications),
    [visibleNotifications]
  );

  return (
    <Layout
      headerClassName="border-b border-alpha-100"
      header={
        <LayoutHeader
          unreadTotalCount={unreadTotalCount}
          tab={pageTab}
          onTabChange={(tab) => setSearchParams({ tab, sub: subTab })}
          unreadOnly={unreadOnly}
          toggleUnreadOnly={() => setUnreadOnly((prev) => !prev)}
          unreadMentionCount={unreadMentionCount?.total_unread || 0}
          unreadAssignedCount={unreadAssignedCount?.total_unread || 0}
          // CRM-1438 has shipped the escalated Messages, so both sub-tabs add up.
          unreadCommunicationCount={
            unreadMessagesSubTabCount + unreadCallsSubTabCount
          }
          unreadPropertyShieldCount={
            unreadPropertyShieldCount?.total_unread || 0
          }
          showPropertyShieldTab={showPropertyShieldTab}
          unreadPhoneNumbersCount={unreadPhoneNumbersCount?.total_unread || 0}
          showPhoneNumbersTab={showPhoneNumbersTab}
          unreadProductsCount={unreadProductsCount?.total_unread || 0}
          showProductsTab={showProductsTab}
          showsProductTabs={showsProductTabs}
          unreadRentalVerificationCount={
            unreadRentalVerificationCount?.total_unread || 0
          }
          unreadApplicationCount={unreadApplicationCount?.total_unread || 0}
          isMarkingAllAsRead={isMarkingAllAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      }
    >
      <Show if={pageTab === NotificationTab.Communication}>
        <Tabs<CommunicationSubTab>
          size="sm"
          tabStyle="outline"
          fullWidthTabs={false}
          className="w-fit"
          tabs={[
            {
              value: CommunicationSubTab.Messages,
              label: "Messages",
              ...(unreadMessagesSubTabCount
                ? {
                    rightElement: {
                      type: "counter" as const,
                      count: unreadMessagesSubTabCount
                    }
                  }
                : {})
            },
            ...(showsCallsSubTab
              ? [
                  {
                    value: CommunicationSubTab.Calls,
                    label: "Calls",
                    ...(unreadCallsSubTabCount
                      ? {
                          rightElement: {
                            type: "counter" as const,
                            count: unreadCallsSubTabCount
                          }
                        }
                      : {})
                  }
                ]
              : [])
          ]}
          active={subTab}
          onChange={(sub) => setSearchParams({ tab: pageTab, sub })}
        />
      </Show>
      <Show if={isRentalVerificationTab}>
        <RentalVerificationFilters
          filter={rentalFilter}
          firstPage={notificationsQuery.data?.pages[0]}
          onChange={setRentalFilter}
        />
      </Show>
      <div className="mt-6 space-y-6">
        {groupedNotifications.length > 0 ? (
          <div className="mt-6 space-y-6">
            {groupedNotifications.map((group) => (
              <div key={group.name}>
                <h2 className="text-base font-medium mb-4 text-content-400">
                  {group.name}
                </h2>
                <Card className="overflow-hidden divide-y divide-content-80">
                  {group.items.map((item) => {
                    if (
                      item.type === NotificationType.InboxMessage ||
                      item.type === NotificationType.InboxMessageDigest
                    )
                      return (
                        <InboxNotificationCard key={item.id} item={item} />
                      );

                    if (
                      item.type === NotificationType.AiCallFollowUp ||
                      item.type === NotificationType.AiCallEscalated
                    )
                      return (
                        <AICallFollowUpNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.MissedCall)
                      return (
                        <MissedCallNotificationCard key={item.id} item={item} />
                      );

                    if (item.type === NotificationType.Voicemail)
                      return (
                        <VoicemailNotificationCard key={item.id} item={item} />
                      );

                    if (
                      item.type === NotificationType.RentalVerificationRequested
                    )
                      return (
                        <RentalVerificationNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (
                      item.type === NotificationType.ListingFraudAlertDetected
                    )
                      return (
                        <FraudAlertNotificationCard key={item.id} item={item} />
                      );

                    if (item.type === NotificationType.EscalatedConversation)
                      return (
                        <EscalatedConversationNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.PhoneNumberPort)
                      return (
                        <PhoneNumberPortNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.ProductRequested)
                      return (
                        <ProductRequestedNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.IdvBypassRequested)
                      return (
                        <IdvBypassNotificationCard key={item.id} item={item} />
                      );

                    if (
                      SHOWING_RESCHEDULE_NOTIFICATION_TYPES.includes(item.type)
                    )
                      return (
                        <ShowingRescheduleNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.SmsNotDelivered)
                      return (
                        <SmsNotDeliveredNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    if (item.type === NotificationType.CallerNeedsHelp)
                      return (
                        <CallerNeedsHelpNotificationCard
                          key={item.id}
                          item={item}
                        />
                      );

                    return (
                      <NotificationCard
                        key={item.id}
                        id={item.id}
                        kind={item.kind}
                        recipientName={item.user.name}
                        authorName={
                          typeof item.author === "string"
                            ? item.author
                            : item.author?.name || ""
                        }
                        readAt={item.read_at}
                        richText={item.rich_text}
                        type={item.type}
                        url={item.url}
                        metadata={item.metadata}
                        createAt={item.created_at}
                      />
                    );
                  })}
                </Card>
              </div>
            ))}
            <div ref={loadMoreRef} className="h-1 w-full" />
          </div>
        ) : (
          <div className="mt-10 flex flex-auto flex-col items-center justify-center">
            <div className="mb-6 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-alpha-40 p-5">
              <HugeiconsIcon
                icon={Notification01Icon}
                size={24}
                className="text-content-500"
              />
            </div>
            <p className="text-sm font-medium text-content-300">
              No notifications yet
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};
